import { useIntl } from 'react-intl'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { classNames } from 'primereact/utils'
import InfoTip from '../../../components/novarium/InfoTip'
import { UrgencyPriorityRowDTO } from '../../../dto/urgencySummary/UrgencySummaryDTO'
import { readablePriority } from '../../ServicesStatistics/periodDetail'
import { formatCount, formatMinutes, formatPercent, NO_VALUE } from '../urgencySummaryFormat'
import { SectionState } from '../useSectionData'

interface UrgencyPrioritiesTabProps {
  priorities: SectionState<UrgencyPriorityRowDTO[]>
}

/**
 * Pestaña «Por código de triaje»: una tabla compacta con los 6 códigos (del más al menos urgente y «Sin código» al final):
 * llegadas, espera media, tiempo objetivo, cuántos esperaron más que el objetivo y qué parte son. Muestra el dato sin
 * semáforo ni veredicto: los objetivos son provisorios (los tiene que validar la dirección médica) y todavía no hay una base
 * clínica para decir cuánto «fuera de objetivo» es mucho; el ⓘ de «Objetivo (min)» lo dice. «Sin código» no tiene objetivo. Si la
 * consulta falla, avisa acá con «Reintentar»; el resto de la pantalla sigue.
 */
export default function UrgencyPrioritiesTab({ priorities }: UrgencyPrioritiesTabProps) {
  const intl = useIntl()
  const rows = priorities.data ?? priorities.stale
  const centered = { className: 'nv-urg-center', headerClassName: 'nv-urg-center' }
  // Los objetivos son provisorios: la advertencia vive en el ⓘ de la cabecera, junto a los números que dependen de ellos
  const targetHeader = (
    <span className="nv-urg-th-tip">
      {intl.formatMessage({ id: 'UrgencySummaryColTarget' })}
      <InfoTip text={intl.formatMessage({ id: 'UrgencySummaryPrioritiesHint' })} />
    </span>
  )

  return (
    <section className="nv-section" aria-labelledby="nv-urg-priorities-title">
      <h2 className="nv-sr-only" id="nv-urg-priorities-title">{intl.formatMessage({ id: 'UrgencySummaryPrioritiesTitle' })}</h2>
      {priorities.status === 'failed' ? (
        <div className="nv-urg-failed" role="status">
          <p className="nv-note">
            <i className="pi pi-info-circle" aria-hidden="true" />
            {intl.formatMessage({ id: 'UrgencySummaryPrioritiesFailed' })}
          </p>
          <Button type="button" link label={intl.formatMessage({ id: 'UrgencySummaryRetry' })} onClick={priorities.retry} />
        </div>
      ) : rows === null ? (
        <p className="nv-note" role="status">
          <i className="pi pi-info-circle" aria-hidden="true" />
          {intl.formatMessage({ id: 'UrgencySummaryPrioritiesLoading' })}
        </p>
      ) : (
        <div className={classNames('nv-load-table', { 'nv-urg-blurred': priorities.status === 'loading' })}>
          <DataTable value={rows} dataKey="order" emptyMessage={intl.formatMessage({ id: 'UrgencySummaryEmpty' })}>
            <Column header={intl.formatMessage({ id: 'UrgencySummaryColCode' })} body={(row: UrgencyPriorityRowDTO) => readablePriority(row.name)} />
            <Column
              header={intl.formatMessage({ id: 'UrgencySummaryColArrivals' })}
              {...centered}
              body={(row: UrgencyPriorityRowDTO) => formatCount(intl, row.arrivals)}
            />
            <Column
              header={intl.formatMessage({ id: 'UrgencySummaryColWait' })}
              {...centered}
              body={(row: UrgencyPriorityRowDTO) => formatMinutes(intl, row.avgWaitMinutes)}
            />
            <Column
              header={targetHeader}
              {...centered}
              body={(row: UrgencyPriorityRowDTO) => (row.targetMinutes === null ? NO_VALUE : formatCount(intl, row.targetMinutes))}
            />
            <Column
              header={intl.formatMessage({ id: 'UrgencySummaryColOverTarget' })}
              {...centered}
              body={(row: UrgencyPriorityRowDTO) => (row.targetMinutes === null ? NO_VALUE : formatCount(intl, row.overTarget))}
            />
            <Column
              header={intl.formatMessage({ id: 'UrgencySummaryColOverTargetPct' })}
              {...centered}
              body={(row: UrgencyPriorityRowDTO) => formatPercent(intl, row.overTargetPct)}
            />
          </DataTable>
        </div>
      )}
    </section>
  )
}
